import type { MatchView } from "@meksiko/shared-types";
import { actingSeat } from "../matchViewHelpers.js";

const MUTE_KEY = "meksiko.muted";

// Served from apps/web/public/sfx/ (URL strings, same convention as
// cards/assets.ts — not bundled as ES module imports). Prefixed with
// import.meta.env.BASE_URL (not a hardcoded leading "/") because Facebook
// Instant Games hosts the built bundle on a nested path, not the domain
// root — a root-absolute URL 404'd there (reproduced live, 2026-09-07).
const SOUND_FILES = {
  cardPlay: `${import.meta.env.BASE_URL}sfx/card-play.wav`,
  turnAlert: `${import.meta.env.BASE_URL}sfx/turn-alert.wav`,
  matchWin: `${import.meta.env.BASE_URL}sfx/match-win.wav`,
  matchLose: `${import.meta.env.BASE_URL}sfx/match-lose.wav`,
} as const;

type SoundName = keyof typeof SOUND_FILES;

// Preloaded lazily, one <audio> element per sound, reused across plays so
// playback isn't delayed by re-fetching the file every time.
const audioCache: Partial<Record<SoundName, HTMLAudioElement>> = {};

function getAudio(name: SoundName): HTMLAudioElement {
  let audio = audioCache[name];
  if (!audio) {
    audio = new Audio(SOUND_FILES[name]);
    audioCache[name] = audio;
  }
  return audio;
}

export function isMuted(): boolean {
  try {
    return localStorage.getItem(MUTE_KEY) === "true";
  } catch {
    return false; // localStorage unavailable (private mode, etc.) — default to sound on
  }
}

export function setMuted(muted: boolean): void {
  try {
    localStorage.setItem(MUTE_KEY, muted ? "true" : "false");
  } catch {
    // Preference just won't persist across reloads — not worth surfacing.
  }
}

export function playSfx(name: SoundName): void {
  if (isMuted()) return;
  const audio = getAudio(name);
  audio.currentTime = 0; // restart even if the same sound is still playing from a rapid previous trigger
  void audio.play().catch(() => {
    // Browsers block autoplay before the user has interacted with the page at
    // all — not worth surfacing, the next user gesture unblocks it.
  });
}

function totalCardsPlayed(view: MatchView): number {
  return view.deal.completedTricks.length * 3 + (view.deal.currentTrick?.cards.length ?? 0);
}

/** Diffs the previous and new match view (as pushed by the server) to decide which, if any, sound effect fits this update. */
export function playMatchUpdateSfx(prev: MatchView | null, next: MatchView): void {
  const sameDeal = prev !== null && prev.deal.dealNumber === next.deal.dealNumber;
  if (sameDeal && totalCardsPlayed(next) > totalCardsPlayed(prev)) {
    playSfx("cardPlay");
  }

  if (next.matchEnded && (!prev || !prev.matchEnded)) {
    playSfx(next.matchWinnerSeat === next.mySeat ? "matchWin" : "matchLose");
    return;
  }

  const prevActingSeat = prev ? actingSeat(prev.deal) : null;
  const nextActingSeat = actingSeat(next.deal);
  if (nextActingSeat === next.mySeat && prevActingSeat !== next.mySeat) {
    playSfx("turnAlert");
  }
}
